import * as React from 'react';
import { Switch } from '@headlessui/react';
import { CgClose, CgMathPlus } from 'react-icons/cg';
import { AiOutlineStar } from 'react-icons/ai';
import SectionTitle from 'components/SectionTitle';
import Card from 'components/Card';
import MiniInput from 'components/MiniInput';
import { TICKER_FLOW_PAGE } from 'constants/pages';
import { useWatchlist } from 'hooks/useWatchlist';

import {
  AddWatchListButton,
  WatchListButton,
  WatchlistTickerCard,
  RemoveTickerButton,
} from './styles';

interface Props {
  isFlowWatchListOnly: boolean;
  setIsFlowWatchListOnly: (isFlowWatchListOnly: boolean) => void;
  isAggregateWatchListOnly: boolean;
  setIsAggregateWatchListOnly: (isAggregateWatchListOnly: boolean) => void;
  onClose?: () => void;
}

const WatchList: React.FC<React.PropsWithChildren<Props>> = ({
  onClose,
  isFlowWatchListOnly,
  setIsFlowWatchListOnly,
  isAggregateWatchListOnly,
  setIsAggregateWatchListOnly,
}) => {
  const [watchListTicker, setWatchListTicker] = React.useState('');
  const {
    isRemovingFromWatchlist,
    isUpdatingWatchlist,
    removeFromWatchlist,
    setTickerWatching,
    watchListData,
  } = useWatchlist();
  const items = (watchListData?.watch_list || []).filter((item) => !item.deletedAt);
  const activeItems = items
    .filter((item) => item.isWatching)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const inactiveItems = items
    .filter((item) => !item.isWatching)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  return (
    <Card className="h-full w-full overflow-hidden" isDark>
      <div className="mb-4 flex items-center">
        <SectionTitle Icon={AiOutlineStar} title="Watchlist" style={{ marginBottom: 0 }} />
        <Switch.Group as="div" className="ml-6 flex items-center">
          <Switch
            checked={isAggregateWatchListOnly}
            onChange={setIsAggregateWatchListOnly}
            className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
          >
            <span
              aria-hidden="true"
              className={`${isAggregateWatchListOnly ? 'bg-yellow-400' : 'bg-gray-400'}
          pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
        `}
            />
            <span
              aria-hidden="true"
              className={`${
                isAggregateWatchListOnly ? 'translate-x-3' : 'translate-x-0'
              } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out`}
            />
          </Switch>
          <Switch.Label as="span" className="ml-3 cursor-pointer">
            <span
              className={`text-xs font-medium ${
                isAggregateWatchListOnly ? 'text-yellow-400' : 'text-gray-400'
              }`}
            >
              Aggregate
            </span>
          </Switch.Label>
        </Switch.Group>
        <Switch.Group as="div" className="ml-6 flex items-center">
          <Switch
            checked={isFlowWatchListOnly}
            onChange={setIsFlowWatchListOnly}
            className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
          >
            <span
              aria-hidden="true"
              className={`${isFlowWatchListOnly ? 'bg-yellow-400' : 'bg-gray-400'}
          pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
        `}
            />
            <span
              aria-hidden="true"
              className={`${
                isFlowWatchListOnly ? 'translate-x-3' : 'translate-x-0'
              } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out`}
            />
          </Switch>
          <Switch.Label as="span" className="ml-3 cursor-pointer">
            <span
              className={`text-xs font-medium ${
                isFlowWatchListOnly ? 'text-yellow-400' : 'text-gray-400'
              }`}
            >
              Flow
            </span>
          </Switch.Label>
        </Switch.Group>
        {onClose && (
          <button
            onClick={onClose}
            className="hover:text-vu ml-auto flex items-center justify-center rounded bg-transparent p-1 text-gray-400 transition-colors duration-200 hover:bg-palette-black-2"
            aria-label="Close watchlist"
          >
            <CgClose size={16} />
          </button>
        )}
      </div>
      <div className="mb-4">
        <form
          className="w-full"
          onSubmit={async (e) => {
            e.preventDefault();

            if (!watchListTicker.trim()) {
              return;
            }

            // Keep what was typed if the write fails so it does not have to be retyped.
            if (await setTickerWatching(watchListTicker, true)) {
              setWatchListTicker('');
            }
          }}
        >
          <MiniInput
            placeholder="Ticker to watch..."
            value={watchListTicker}
            onChange={(e) => setWatchListTicker(e.target.value.toUpperCase())}
            reset={() => setWatchListTicker('')}
            button={
              <AddWatchListButton
                disabled={isUpdatingWatchlist}
                className="flex-shrink-0 items-center justify-center px-4 py-2 text-xs"
                type="submit"
              >
                {isUpdatingWatchlist ? 'Loading...' : 'Add'}
              </AddWatchListButton>
            }
          />
        </form>
      </div>
      <div className="flex justify-between"></div>
      <div className="flex h-full max-h-full flex-col overflow-hidden pb-20">
        <div className="flex-shrink flex-grow overflow-y-auto">
          {!!activeItems && activeItems.length > 0 && (
            <div className="mb-4">
              <div className="mb-1 text-xs font-semibold text-gray-400">Active Items</div>
              <div className="grid grid-cols-3 gap-2">
                {activeItems.map((item) => (
                  <WatchlistTickerCard
                    key={item.ticker}
                    className="flex items-center justify-between text-xs"
                    style={{ padding: '.5rem', paddingLeft: '.75rem' }}
                  >
                    <div>
                      <a href={`${TICKER_FLOW_PAGE}/${item.ticker}`} target="_blank">
                        {item.ticker}
                      </a>
                    </div>
                    <WatchListButton onClick={() => void setTickerWatching(item.ticker, false)}>
                      <CgClose />
                    </WatchListButton>
                  </WatchlistTickerCard>
                ))}
              </div>
            </div>
          )}
          {!!inactiveItems && inactiveItems.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold text-gray-400">Inactive Items</div>
              <div className="grid grid-cols-3 gap-2">
                {inactiveItems.map((item) => (
                  <WatchlistTickerCard
                    key={item.ticker}
                    className="flex items-center justify-between text-xs"
                    style={{ padding: '.5rem', paddingLeft: '.75rem' }}
                  >
                    <div>
                      <a href={`${TICKER_FLOW_PAGE}/${item.ticker}`} target="_blank">
                        {item.ticker}
                      </a>
                    </div>
                    <div>
                      <RemoveTickerButton
                        onClick={() => {
                          if (!isRemovingFromWatchlist) {
                            void removeFromWatchlist(item.id);
                          }
                        }}
                      >
                        Remove
                      </RemoveTickerButton>
                    </div>
                    <WatchListButton onClick={() => void setTickerWatching(item.ticker, true)}>
                      <CgMathPlus />
                    </WatchListButton>
                  </WatchlistTickerCard>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default WatchList;
